import React from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import AdminSurface from '../../../../components/admin/AdminSurface';
import AdminSearchField from '../../../../components/admin/AdminSearchField';
import type { DashboardGamesRow } from '../../../../types/features/games';
import {
  sortDashboardGamesRows,
  type DashboardGamesSortDirection,
  type DashboardGamesSortKey,
} from '../mappers/dashboardGamesMappers';
import {
  adminAlertSx,
  adminAlertToneSx,
  adminCardHeaderSx,
  adminCheckboxSx,
  adminCombineSx,
  adminEmptyStateSx,
  adminInlineControlLabelSx,
  adminTableSx,
} from '../../../../styles/adminComponentStyles';

interface DashboardGamesTableProps {
  rows: DashboardGamesRow[];
  search: string;
  loading: boolean;
  error: string | null;
  showAllRegions: boolean;
  onShowAllRegionsChange: (checked: boolean) => void;
  onSearchChange: (value: string) => void;
  onStateOpen: (stateId: string) => void;
  savingStateCode: string | null;
  onProviderSave: (stateCode: string, provider: string) => Promise<void> | void;
}

const columns: Array<{
  align?: 'left' | 'right';
  key: DashboardGamesSortKey;
  label: string;
  width: string;
}> = [
  { key: 'countryLabel', label: 'Country', width: '26%' },
  { key: 'stateLabel', label: 'State', width: '26%' },
  { key: 'lotterySoftwareProvider', label: 'Provider', width: '28%' },
  { key: 'gamesCount', label: 'Games', align: 'right', width: '10%' },
  { key: 'gamesWithUpcCount', label: 'With UPC', align: 'right', width: '10%' },
];

const DashboardGamesTable: React.FC<DashboardGamesTableProps> = ({
  rows,
  search,
  loading,
  error,
  showAllRegions,
  onShowAllRegionsChange,
  onSearchChange,
  onStateOpen,
  savingStateCode,
  onProviderSave,
}) => {
  const [sort, setSort] = React.useState<{ key: DashboardGamesSortKey; direction: DashboardGamesSortDirection }>({
    key: 'countryLabel',
    direction: 'asc',
  });
  const [editProviders, setEditProviders] = React.useState<Record<string, string>>({});

  const visibleRows = React.useMemo(
    () => sortDashboardGamesRows(rows, sort.key, sort.direction),
    [rows, sort.direction, sort.key],
  );

  const handleSort = React.useCallback((key: DashboardGamesSortKey) => {
    setSort((currentSort) => ({
      key,
      direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handleProviderChange = React.useCallback((stateCode: string, value: string) => {
    setEditProviders((current) => ({ ...current, [stateCode]: value.slice(0, 30) }));
  }, []);

  const handleProviderCommit = React.useCallback(async (state: DashboardGamesRow) => {
    const draft = (editProviders[state.stateCode] ?? state.lotterySoftwareProvider).slice(0, 30);
    if (draft === state.lotterySoftwareProvider) {
      return;
    }
    await onProviderSave(state.stateCode, draft);
    setEditProviders((current) => {
      const { [state.stateCode]: _ignored, ...rest } = current;
      return rest;
    });
  }, [editProviders, onProviderSave]);

  return (
    <>
      <AdminSurface sx={{ border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgb(34 43 69 / 8%)', overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Box sx={adminCombineSx(adminCardHeaderSx, {
            mb: 1,
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
          })}>
            <AdminSearchField
              placeholder="Search states"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              sx={{ width: { xs: '100%', sm: 280 } }}
              inputProps={{ 'aria-label': 'Search states' }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={showAllRegions}
                  onChange={(event) => onShowAllRegionsChange(event.target.checked)}
                  size="small"
                  sx={adminCheckboxSx}
                />
              )}
              label="Show all regions"
              sx={adminInlineControlLabelSx}
            />
          </Box>
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            aria-label="states table"
            sx={adminCombineSx(adminTableSx, {
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                padding: '8px 12px !important',
              },
              '& .MuiTableHead-root .MuiTableCell-head': {
                textTransform: 'uppercase',
              },
              '& .MuiTableBody-root .MuiTableRow-root': {
                cursor: 'pointer',
                height: 42,
              },
            })}
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  const active = sort.key === column.key;

                  return (
                    <TableCell
                      key={column.key}
                      align={column.align}
                      sortDirection={active ? sort.direction : false}
                      sx={{ width: column.width }}
                    >
                      <TableSortLabel
                        active={active}
                        direction={active ? sort.direction : 'asc'}
                        hideSortIcon={false}
                        onClick={() => handleSort(column.key)}
                        sx={{
                          justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
                          width: '100%',
                        }}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ border: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ border: 0, p: 1.5 }}>
                    <Box sx={adminCombineSx(adminEmptyStateSx, { border: 0, minHeight: 80 })}>
                      <Typography className="AdminEmptyState-title">No states found</Typography>
                      <Typography className="AdminEmptyState-copy">Try another search or show all regions.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((state) => (
                  <TableRow
                    key={state.stateCode}
                    hover
                    onClick={() => onStateOpen(state.stateCode)}
                  >
                    <TableCell sx={{ width: '26%' }}>{state.countryLabel}</TableCell>
                    <TableCell sx={{ width: '26%' }}>{state.stateLabel}</TableCell>
                    <TableCell
                      sx={{ width: '28%' }}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <TextField
                        size="small"
                        value={editProviders[state.stateCode] ?? state.lotterySoftwareProvider}
                        onChange={(event) => handleProviderChange(state.stateCode, event.target.value)}
                        onBlur={() => { void handleProviderCommit(state); }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void handleProviderCommit(state);
                          }
                        }}
                        placeholder="(none)"
                        inputProps={{ maxLength: 30 }}
                        fullWidth
                        variant="outlined"
                        sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
                        InputProps={{
                          endAdornment: savingStateCode === state.stateCode ? (
                            <CircularProgress size={14} />
                          ) : undefined,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ width: '10%' }}>{state.gamesCount}</TableCell>
                    <TableCell align="right" sx={{ width: '10%' }}>{state.gamesWithUpcCount}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </AdminSurface>
      {error && (
        <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mt: 1.5 })}>
          {error}
        </Alert>
      )}
    </>
  );
};

export default React.memo(DashboardGamesTable);
